import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  Divider,
  Alert,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  InputAdornment,
  Switch,
  SwitchProps,
  styled,
  useTheme,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Camera } from 'react-camera-pro';
import DateTimePickerWithTimezone from 'components/DateTimePickerWithTimezone';
import MultiFileUploader from './fileUpload';
import useApi from 'hooks/useApi';
import { InputComponents } from 'types/auth';
import ResourceDetailsDialog from 'components/ResourceRating/ResourceDetailsDialog';
import JumioVerificationBadge from 'components/JumioVerificationBadge';
import useJumioVerifications from 'hooks/useJumioVerifications';
import StartChatPanel from 'components/chat/StartChatPanel';
import InlineChatWindow from 'components/chat/InlineChatWindow';
import { ChatThread } from 'types/chat';
import AttachmentsDialog from './AttachmentsDialog';

// ─── Styled Components ────────────────────────────────────────────────────────

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  display: 'flex',
  '&:active .MuiSwitch-thumb': { width: 22 },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': { backgroundColor: '#65C466', opacity: 1, border: 0 },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 22,
    height: 22,
    borderRadius: 11,
    transition: theme.transitions.create(['width'], { duration: 200 }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    opacity: 1,
    backgroundColor: theme.palette.mode === 'dark' ? '#39393D' : '#E9E9EA',
    boxSizing: 'border-box',
  },
}));

// ─── Types ────────────────────────────────────────────────────────────────────

interface FieldValue {
  value: any[];
  files: any[];
}

interface EventDataItem {
  _id: string;
  uid: string;
  field: string;
  question: string;
  inputComponent: InputComponents | string;
  description: string;
  value: FieldValue;
  status: boolean;
  children: any[];
  settings: {
    required?: boolean;
    multiple?: boolean;
    isTimeRequired?: boolean;
    isMultiline?: boolean;
    rows?: number;
    includeTimezone?: boolean;
    isEditable?: boolean;
    cameraMode?: string;
    noOfImages?: number;
    minDuration?: number;
    min?: number;
    resourceType?: string;
    dropdownSource?: string;
    label?: string;
    [key: string]: any;
  };
  options?: string[];
  resourceType?: string;
  dropdownSource?: string;
}

interface CompletedStep {
  stepUid: string;
  stepTitle: string;
  eventId: string;
  eventData: EventDataItem[];
  eventStatus: string;
  stepStatus: string;
  eventType: string;
  submittedAt: string;
  submittedBy: { firstName: string; lastName: string; id: string };
  componentSchema: {
    fields: {
      uid: string;
      question: string;
      inputComponent: string;
      options?: string[];
      resourceType?: string;
      dropdownSource?: string;
      settings: {
        isEditable?: boolean;
        required?: boolean;
        multiple?: boolean;
        isTimeRequired?: boolean;
        isMultiline?: boolean;
        rows?: number;
        includeTimezone?: boolean;
        cameraMode?: string;
        noOfImages?: number;
        label?: string;
        [key: string]: any;
      };
    }[];
  };
}

interface EditTransactionModalProps {
  open: boolean;
  onClose: () => void;
  transactionItem: any;
  completedSteps: CompletedStep[];
  onSaveSuccess?: () => void;
}

interface Field {
  field: string;
  description: any;
  question: string;
  inputComponent: InputComponents;
  hasAddIcon?: boolean;
  options?: string[];
  resourceType?: string;
  resourceList?: string[];
  dropdownSource?: string;
  settings?: {
    noOfImages?: number;
    required?: boolean;
    multiple?: boolean;
    minDuration?: number;
    min?: number;
    resourceType?: string;
    dropdownSource?: string;
    isTimeRequired?: boolean;
    isMultiline?: boolean;
    rows?: number;
    isEditable?: boolean;
    cameraMode?: string;
    label?: string;
    [key: string]: any;
  };
  uid: string;
}

// ─── ResourceSelector Component ──────────────────────────────────────────────

interface ResourceSelectorProps {
  field: Field;
  value: any;
  onChange: (value: any) => void;
  resourceType?: string;
  resourceList?: string[];
  multiple?: boolean;
}

const ResourceSelector: React.FC<ResourceSelectorProps> = ({ field, value, onChange, resourceType, resourceList, multiple = false }) => {
  const { searchResources, getResourceTypeById, createResourceData, showToast, user } = useApi();

  const countryCodes = [
    '1', '7', '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49',
    '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86', '87', '087',
    '90', '91', '92', '93', '94', '95', '98', '212', '213', '216', '218', '220', '221', '222', '223', '224', '225',
    '226', '227', '228', '229', '230', '231', '232', '233', '234', '235', '236', '237', '238', '239', '240', '241',
    '242', '243', '244', '245', '246', '248', '249', '250', '251', '252', '253', '254', '255', '256', '257', '258',
    '260', '261', '262', '263', '264', '265', '266', '267', '268', '269', '290', '291', '297', '298', '299', '350',
    '351', '352', '353', '354', '355', '356', '357', '358', '359', '370', '371', '372', '373', '374', '375', '376',
    '377', '378', '380', '381', '382', '383', '385', '386', '387', '389', '420', '421', '423', '500', '501', '502',
    '503', '504', '505', '506', '507', '508', '509', '590', '591', '592', '593', '594', '595', '596', '597', '598',
    '599', '670', '672', '673', '674', '675', '676', '677', '678', '679', '680', '681', '682', '683', '684', '685',
    '686', '687', '688', '689', '690', '691', '692', '850', '852', '853', '855', '856', '880', '886', '960', '961',
    '962', '963', '964', '965', '966', '967', '968', '970', '971', '972', '973', '974', '975', '976', '977', '992',
    '993', '994', '995', '996', '998',
  ];

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingResource, setViewingResource] = useState<any>(null);
  const [addFormFields, setAddFormFields] = useState<any[]>([]);
  const [addFormValues, setAddFormValues] = useState<any>({});
  const [addingResource, setAddingResource] = useState(false);
  const [selectedResources, setSelectedResources] = useState<any[]>([]);

  const handleViewDialogClose = () => setShowViewDialog(false);

  React.useEffect(() => {
    if (Array.isArray(value) && multiple) {
      setSelectedResources(value.filter(v => v && (v.id || v._id)));
    } else if (value && !multiple && (value.id || value._id)) {
      setSelectedResources([value]);
    } else {
      setSelectedResources([]);
    }
  }, [value, multiple]);

  React.useEffect(() => {
    setSearchResults([]);
    if (searchTerm.trim() === '') return;
    const hasAnyType = !!resourceType || (resourceList && resourceList.length > 0);
    const delayedSearch = setTimeout(() => {
      if (searchTerm && hasAnyType) handleSearch();
    }, 300);
    return () => clearTimeout(delayedSearch);
  }, [searchTerm, resourceType, resourceList]);

  const handleSearch = async () => {
    // Search column (resourceList) is authoritative. Primary is only the auto-update target.
    // Backward-compat: fall back to [resourceType] when resourceList is unset.
    const effectiveTypes: string[] = (resourceList && resourceList.length > 0)
      ? resourceList
      : (resourceType ? [resourceType] : []);
    if (effectiveTypes.length === 0) { setSearchResults([]); return; }
    if (!searchTerm.trim()) { setSearchResults([]); return; }
    setLoading(true);
    try {
      const response = await searchResources(null, searchTerm.trim(), effectiveTypes);
      if (response?.success && Array.isArray(response.data?.data)) {
        setSearchResults(response.data.data || []);
      } else {
        setSearchResults([]);
      }
    } catch {
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectResource = (resource: any) => {
    if (!resource || (!resource.id && !resource._id)) return;
    const resourceId = resource.id || resource._id;
    const resourceWithType = {
      ...resource,
      category: resource.category || resource.resourceTypeId?.category || resource.resourceType?.category || false,
    };
    if (multiple) {
      const isSelected = selectedResources.some((r) => (r.id || r._id) === resourceId);
      const newSelection = isSelected
        ? selectedResources.filter((r) => (r.id || r._id) !== resourceId)
        : [...selectedResources, resourceWithType];
      setSelectedResources(newSelection);
      onChange(newSelection);
    } else {
      setSelectedResources([resourceWithType]);
      onChange(resourceWithType);
    }
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleRemoveResource = (resourceId: string) => {
    if (!resourceId) return;
    const newSelection = selectedResources.filter((r) => (r.id || r._id) !== resourceId);
    setSelectedResources(newSelection);
    onChange(multiple ? newSelection : null);
  };

  const handleViewResource = (resource: any) => {
    setViewingResource(resource);
    setShowViewDialog(true);
  };

  const handleAddResource = async () => {
    if (!resourceType) return;
    try {
      const response = await getResourceTypeById(resourceType);
      if (response?.success && response.data?.fields) {
        const filteredFields = response.data.fields.filter((f: any) => f.dropdownSource !== 'teams');
        setAddFormFields(filteredFields);
        const initialValues: any = {};
        filteredFields.forEach((f: any) => { initialValues[f.name] = ''; });
        setAddFormValues(initialValues);
        setShowAddDialog(true);
      }
    } catch (error) {
      console.error('Error fetching resource type fields:', error);
    }
  };

  const handleSaveResource = async () => {
    if (!resourceType) return;
    setAddingResource(true);
    try {
      const response = await createResourceData(resourceType, addFormValues);
      if (response?.success) {
        const newResource = { ...response.data, resourceType: response.data.resourceType || resourceType };
        showToast('Resource created successfully', 'success');
        if (multiple) {
          const newSelection = [...selectedResources, newResource];
          setSelectedResources(newSelection);
          onChange(newSelection);
        } else {
          setSelectedResources([newResource]);
          onChange(newResource);
        }
        setShowAddDialog(false);
        setAddFormValues({});
        setSearchTerm('');
      } else {
        showToast(response?.message || 'Failed to create resource', 'error');
      }
    } catch (error: any) {
      showToast(error?.message || 'Failed to create resource', 'error');
    } finally {
      setAddingResource(false);
    }
  };

  const getDisplayName = (resource: any) => {
    if (!resource?.fields || typeof resource.fields !== 'object') {
      return resource?.name || `Resource ${resource?.id || resource?._id || 'Unknown'}`;
    }
    const f = resource.fields;

    // `fields` is keyed by the resource-type's display labels, so match them on
    // letters alone instead of assuming camelCase keys.
    const norm = (k: string) => k.toLowerCase().replace(/[^a-z]/g, '');
    const pick = (...names: string[]): string => {
      const target = names.map(norm);
      for (const [key, val] of Object.entries(f)) {
        if (target.includes(norm(key)) && (typeof val === 'string' || typeof val === 'number') && String(val).trim()) {
          return String(val).trim();
        }
      }
      return '';
    };

    // Only labels that unambiguously hold a whole name short-circuit. A plain
    // `Name` label does not: types commonly pair it with a separate `Last Name`,
    // and returning on `Name` alone is what dropped the surname here.
    const full = pick('fullName', 'displayName');
    if (full) return full;

    const first = pick('firstName', 'first', 'givenName', 'name');
    const last = pick('lastName', 'last', 'secondName', 'surname', 'familyName');
    if (first || last) {
      return first && last && first !== last ? `${first} ${last}` : (first || last);
    }

    const fieldValues = Object.values(resource.fields);
    return fieldValues.length > 0 ? String(fieldValues[0]) : 'Unnamed Resource';
  };

  // Jumio state for everyone this picker can show, so a driver's verification is
  // visible at the moment of assignment.
  const verificationOf = useJumioVerifications(
    React.useMemo(() => [...selectedResources, ...searchResults], [selectedResources, searchResults]),
  );

  return (
    <Box>
      {selectedResources.length > 0 && (
        <Box mb={1.5}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
            Selected {multiple ? 'Resources' : 'Resource'}
          </Typography>
          {selectedResources.map((resource) => (
            <Box key={resource.id || resource._id} sx={{ py: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {getDisplayName(resource)}
                <JumioVerificationBadge verification={verificationOf(resource)} />
              </Typography>
              <Box display="flex" gap={0.5}>
                <IconButton size="small" onClick={() => handleViewResource(resource)} sx={{ p: 0.5 }}>
                  <InsertDriveFileIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => handleRemoveResource(resource.id || resource._id)} sx={{ p: 0.5 }}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <TextField
        fullWidth size="small" placeholder="Search resources..." value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{ endAdornment: <InputAdornment position="end">{loading && <CircularProgress size={16} />}</InputAdornment> }}
        sx={{ mb: 1 }}
      />

      {searchTerm && (
        <Box>
          {searchResults.length > 0 ? (
            <Box maxHeight={200} overflow="auto" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
              {searchResults
                .filter((r) => !selectedResources.some((s) => s.id === r.id))
                .map((resource) => (
                  <Box key={resource.id || resource._id} sx={{ py: '5px', px: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' }, '&:hover': { bgcolor: 'action.hover' } }}>
                    <Box flex={1} display="flex" alignItems="center" gap={1} minWidth={0}>
                      <Typography
                        variant="body2"
                        title={getDisplayName(resource)}
                        sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {getDisplayName(resource)}
                      </Typography>
                      <JumioVerificationBadge verification={verificationOf(resource)} />
                      {resource.resourceTypeId?.name && (
                        <Typography
                          variant="caption"
                          title={resource.resourceTypeId.name}
                          sx={{
                            flexShrink: 1,
                            minWidth: 0,
                            maxWidth: '40%',
                            px: 0.75,
                            py: 0.25,
                            fontSize: '0.65rem',
                            borderRadius: 0.75,
                            backgroundColor: 'action.selected',
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {resource.resourceTypeId.name}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" gap={0.5}>
                      <IconButton size="small" onClick={() => handleViewResource(resource)} sx={{ p: 0.5 }}>
                        <InsertDriveFileIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      <Button size="small" variant="outlined" onClick={() => handleSelectResource(resource)} sx={{ minWidth: 'auto', px: 1, py: 0.25, fontSize: '0.7rem' }}>
                        Select
                      </Button>
                    </Box>
                  </Box>
                ))}
            </Box>
          ) : (
            !loading && (
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 1, mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>No results found</Typography>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddResource} sx={{ px: 1.5 }}>
                  Add New
                </Button>
              </Box>
            )
          )}
        </Box>
      )}

      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth slotProps={{ backdrop: { sx: { zIndex: 1300 } } }}>
        <DialogTitle>Add New Resource</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {addFormFields.map((f: any, index: number) => (
              <Box key={index}>
                {f.type === 'dropdown' && (f.dropdownSource === 'countryCode' || f.settings?.dropdownSource === 'countryCode') ? (
                  <TextField fullWidth select label={f.name} required={f.required} value={addFormValues[f.name] || ''} onChange={(e) => setAddFormValues((prev: any) => ({ ...prev, [f.name]: e.target.value }))}>
                    <MenuItem value="">Please select</MenuItem>
                    {countryCodes.map((code) => <MenuItem key={code} value={code}>{code}</MenuItem>)}
                  </TextField>
                ) : (
                  <TextField fullWidth label={f.name} required={f.required} type={f.type === 'number' ? 'number' : 'text'} multiline={f.type === 'textarea'} rows={f.type === 'textarea' ? 3 : 1} value={addFormValues[f.name] || ''} onChange={(e) => setAddFormValues((prev: any) => ({ ...prev, [f.name]: e.target.value }))} />
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveResource} disabled={addingResource || addFormFields.some((f: any) => f.required && !addFormValues[f.name]?.trim())}>
            {addingResource ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ResourceDetailsDialog open={showViewDialog} onClose={handleViewDialogClose} resource={viewingResource} allowRating={false} userId={user?.uid || user?.id || ''} />
    </Box>
  );
};

// ─── Helper Functions ─────────────────────────────────────────────────────────

const transformPayloadForApi = (
  groupedUpdates: Array<{ eventId: string; stepUid: string; stepTitle: string; data: any[] }>,
  steps: CompletedStep[]
): any[] => {
  return groupedUpdates.map((update) => {
    const step = steps.find((s) => s.eventId === update.eventId);
    if (!step) { console.warn(`Step not found for eventId: ${update.eventId}`); return null; }
    return {
      eventId: update.eventId,
      eventData: update.data,
      eventStatus: step.eventStatus || 'PENDING',
      eventType: step.eventType || 'FORM',
    };
  }).filter((item) => item !== null);
};

const patchEventValuesIntoSchema = (schemaFields: Field[], eventData: EventDataItem[]): Record<string, any> => {
  const patchedValues: Record<string, any> = {};
  const schemaFieldMap = new Map<string, Field>();
  schemaFields.forEach((field) => schemaFieldMap.set(field.question, field));

  schemaFields.forEach((field) => {
    switch (field.inputComponent) {
      case InputComponents.CHECKBOX: patchedValues[field.question] = []; break;
      case InputComponents.CAMERA: patchedValues[field.question] = []; break;
      case InputComponents.RESOURCE: patchedValues[field.question] = field.settings?.multiple ? [] : null; break;
      case InputComponents.FILE:
      case InputComponents.ESIGN: patchedValues[field.question] = []; break;
      case InputComponents.SWITCH: patchedValues[field.question] = { value: false }; break;
      case InputComponents.DATE: patchedValues[field.question] = null; break;
      case InputComponents.TEXT: patchedValues[field.question] = field.settings?.multiple ? [] : ''; break;
      default: patchedValues[field.question] = '';
    }
  });

  if (Array.isArray(eventData)) {
    eventData.forEach((dataField: any) => {
      if (dataField && dataField.question) {
        const fieldValue = dataField.value?.value;
        const schemaField = schemaFieldMap.get(dataField.question);
        switch (dataField.inputComponent) {
          case InputComponents.TEXT: {
            const isMultiple = dataField.settings?.multiple || schemaField?.settings?.multiple;
            // A counter field is a comma-separated list typed into one box, and its
            // stored value can carry several entries. Taking [0] hid the rest and,
            // on save, wrote them away. Join instead. (Same as editForm.tsx.)
            const isCounter = dataField.settings?.isCounter || schemaField?.settings?.isCounter;
            if (isMultiple && Array.isArray(fieldValue)) {
              patchedValues[dataField.question] = fieldValue;
            } else if (isCounter && Array.isArray(fieldValue)) {
              patchedValues[dataField.question] = fieldValue.filter(Boolean).join(', ');
            } else {
              patchedValues[dataField.question] = Array.isArray(fieldValue) ? fieldValue[0] || '' : fieldValue || '';
            }
            break;
          }
          case InputComponents.RESOURCE:
            if (fieldValue) {
              const isMultiple = dataField.settings?.multiple || schemaField?.settings?.multiple;
              if (isMultiple) {
                patchedValues[dataField.question] = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
              } else {
                patchedValues[dataField.question] = Array.isArray(fieldValue) ? (fieldValue[0] || null) : fieldValue;
              }
            }
            break;
          case InputComponents.DATE:
            if (fieldValue) {
              try {
                let dateVal = fieldValue;
                if (Array.isArray(fieldValue) && fieldValue.length > 0) dateVal = fieldValue[0];
                if (dateVal) patchedValues[dataField.question] = dateVal;
              } catch { patchedValues[dataField.question] = null; }
            }
            break;
          case InputComponents.CHECKBOX:
            patchedValues[dataField.question] = Array.isArray(fieldValue) ? fieldValue : [];
            break;
          case InputComponents.CAMERA:
            patchedValues[dataField.question] = dataField.value?.files || [];
            break;
          case InputComponents.FILE:
          case InputComponents.ESIGN:
            patchedValues[dataField.question] = dataField.value?.files || [];
            break;
          case InputComponents.SWITCH: {
            let switchValue = false;
            const possibleValues = [fieldValue, dataField.value, dataField.value?.checked, dataField.checked];
            for (const val of possibleValues) {
              if (val !== undefined && val !== null) {
                if (typeof val === 'boolean') { switchValue = val; break; }
                else if (Array.isArray(val) && val.length > 0) {
                  const firstVal = val[0];
                  if (typeof firstVal === 'boolean') { switchValue = firstVal; break; }
                  else if (firstVal === 'true') { switchValue = true; break; }
                  else if (firstVal === 'false') { switchValue = false; break; }
                  else if (firstVal === 1 || firstVal === '1') { switchValue = true; break; }
                  else if (firstVal === 0 || firstVal === '0') { switchValue = false; break; }
                } else if (val === 'true') { switchValue = true; break; }
                else if (val === 'false') { switchValue = false; break; }
                else if (val === 1 || val === '1') { switchValue = true; break; }
                else if (val === 0 || val === '0') { switchValue = false; break; }
              }
            }
            patchedValues[dataField.question] = { value: switchValue };
            break;
          }
          case InputComponents.SELECT:
          case InputComponents.RADIO:
            // Handle array values (take first element), JSON strings, or direct values
            let selectValue = fieldValue;
            if (Array.isArray(selectValue) && selectValue.length > 0) {
              selectValue = selectValue[0];
            }
            // If it's a JSON string,try to parse it
            if (typeof selectValue === 'string' && selectValue.startsWith('[')) {
              try {
                const parsed = JSON.parse(selectValue);
                if (Array.isArray(parsed) && parsed.length > 0) {
                  selectValue = parsed[0];
                }
              } catch (e) {
                // Keep original value if parsing fails
              }
            }
            patchedValues[dataField.question] = selectValue || '';
            break;
          case InputComponents.NUMBER:
            patchedValues[dataField.question] = Array.isArray(fieldValue) ? (fieldValue[0] ?? '') : (fieldValue ?? '');
            break;
          default:
            patchedValues[dataField.question] = fieldValue || '';
        }
      }
    });
  }

  return patchedValues;
};

const groupFieldsByStep = (
  steps: CompletedStep[],
  schemaFields: Field[],
  values: Record<string, any>,
  eventDataMap: Map<string, EventDataItem>
): Array<{ eventId: string; stepUid: string; stepTitle: string; data: any[] }> => {
  const groupedUpdates: Array<{ eventId: string; stepUid: string; stepTitle: string; data: any[] }> = [];

  steps.forEach((step) => {
    const stepFields = schemaFields.filter((field) =>
      step.componentSchema?.fields?.some((sf) => sf.uid === field.uid)
    );
    if (stepFields.length === 0) return;

    const stepData = stepFields.map((field) => {
      const fieldValue = values[field.question];
      const originalEventData = Array.from(eventDataMap.values()).find((ed) => ed.field === field.uid);

      if (field.inputComponent === InputComponents.FILE) {
        return { uid: originalEventData?.uid, field: field.uid, question: field.question, description: field.description ?? null, value: { value: '', files: Array.isArray(fieldValue) ? fieldValue : [] }, inputComponent: field.inputComponent, children: [] };
      }
      if (field.inputComponent === InputComponents.RESOURCE) {
        let serializedValue: any = null;
        if (fieldValue) {
          if (Array.isArray(fieldValue)) {
            serializedValue = fieldValue.map((r: any) => r ? { id: r.id || r._id, fields: r.fields || {}, category: r.category || r.resourceType?.category || r.resourceTypeId?.category } : null).filter(Boolean);
          } else {
            serializedValue = { id: fieldValue.id || fieldValue._id, fields: fieldValue.fields || {}, category: fieldValue.category || fieldValue.resourceTypeId?.category || fieldValue.resourceType?.category };
          }
        }
        return { uid: originalEventData?.uid, field: field.uid, question: field.question, description: field.description ?? null, value: { value: serializedValue, files: [] }, inputComponent: field.inputComponent, children: [] };
      }
      if (field.inputComponent === InputComponents.TEXT) {
        let valueToSave = fieldValue;
        if (field.settings?.multiple) {
          valueToSave = Array.isArray(fieldValue) ? fieldValue : fieldValue ? [fieldValue] : [];
        } else if (field.settings?.isCounter) {
          // Mirror of the load above: the single box holds the whole
          // comma-separated list, so split it back out instead of keeping [0].
          valueToSave = (Array.isArray(fieldValue) ? fieldValue : [fieldValue])
            .flatMap((entry: any) => String(entry ?? '').split(','))
            .map((entry: string) => entry.trim())
            .filter(Boolean);
        } else {
          valueToSave = Array.isArray(fieldValue) ? [fieldValue[0] || ''] : fieldValue ? [fieldValue] : [];
        }
        return { uid: originalEventData?.uid, field: field.uid, question: field.question, description: field.description ?? null, value: { value: valueToSave, files: [] }, inputComponent: field.inputComponent, children: [] };
      }
      if (field.inputComponent === InputComponents.SWITCH) {
        return { uid: originalEventData?.uid, field: field.uid, question: field.question, description: field.description ?? null, value: { value: fieldValue?.value ?? false, files: [] }, inputComponent: field.inputComponent, children: [] };
      }
      if (field.inputComponent === InputComponents.DATE) {
        let dateValue = null;
        if (fieldValue) {
          if (typeof fieldValue === 'object' && fieldValue !== null && 'date' in fieldValue && 'timezone' in fieldValue) {
            dateValue = fieldValue;
          } else {
            try {
              let parsedDate = fieldValue instanceof Date ? fieldValue : typeof fieldValue === 'string' ? new Date(fieldValue) : fieldValue;
              if (parsedDate && !isNaN(parsedDate.getTime())) {
                dateValue = { date: parsedDate.toISOString(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', includeTime: field.settings?.isTimeRequired || false, includeTimezone: field.settings?.includeTimezone || false };
              }
            } catch { dateValue = null; }
          }
        }
        return { uid: originalEventData?.uid, field: field.uid, question: field.question, description: field.description ?? null, value: { value: dateValue ? [dateValue] : [], files: [] }, inputComponent: field.inputComponent, children: [] };
      }
      if (field.inputComponent === InputComponents.CHECKBOX) {
        return { uid: originalEventData?.uid, field: field.uid, question: field.question, description: field.description ?? null, value: { value: Array.isArray(fieldValue) ? fieldValue : [], files: [] }, inputComponent: field.inputComponent, children: [] };
      }
      if (field.inputComponent === InputComponents.CAMERA && field.settings?.cameraMode === 'IMAGE') {
        return { uid: originalEventData?.uid, field: field.uid, question: field.question, description: field.description ?? null, value: { value: '', files: Array.isArray(fieldValue) ? fieldValue : [] }, inputComponent: field.inputComponent, children: [] };
      }
      if (field.inputComponent === InputComponents.ESIGN) {
        return { uid: originalEventData?.uid, field: field.uid, question: field.question, description: field.description ?? null, value: { value: '', files: Array.isArray(fieldValue) ? fieldValue : [] }, inputComponent: field.inputComponent, children: [] };
      }
      if (field.inputComponent === InputComponents.SELECT || field.inputComponent === InputComponents.RADIO) {
        return { uid: originalEventData?.uid, field: field.uid, question: field.question, description: field.description ?? null, value: { value: fieldValue || '', files: [] }, inputComponent: field.inputComponent, children: [] };
      }
      return { uid: originalEventData?.uid, field: field.uid, question: field.question, description: field.description ?? null, value: { value: fieldValue, files: [] }, inputComponent: field.inputComponent, children: [] };
    });

    groupedUpdates.push({ eventId: step.eventId, stepUid: step.stepUid, stepTitle: step.stepTitle, data: stepData });
  });

  return groupedUpdates;
};

// ─── Main Modal ───────────────────────────────────────────────────────────────

const EditTransactionModal: React.FC<EditTransactionModalProps> = ({
  open,
  onClose,
  transactionItem,
  completedSteps,
  onSaveSuccess,
}) => {
  const { showToast, bulkUpdateEvents, getSignedUrl, uploadAsset } = useApi();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [steps, setSteps] = useState<CompletedStep[]>([]);
  const [passwordVisibility, setPasswordVisibility] = useState<Record<string, boolean>>({});
  const [cameraField, setCameraField] = useState<string>('');
  const cameraRef = useRef<any>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [transactionId, setTransactionId] = useState<string>('');
  const [currentChatThread, setCurrentChatThread] = useState<ChatThread | null>(null);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

  const handleTogglePassword = (key: string) =>
    setPasswordVisibility((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleChatCreated = (newThread: ChatThread) => {
    setCurrentChatThread(newThread);
    showToast('Chat created successfully', 'success');
  };

  const handleBackToCreateChat = () => setCurrentChatThread(null);

  const handleDownloadFile = async (file: any, fileName: string) => {
    const fileId = file._id || file.id;
    if (!fileId || !file.file_path) {
      showToast('Unable to download file', 'error');
      return;
    }

    setDownloadingFileId(fileId);
    try {
      const response = await getSignedUrl(file);
      if (response?.success && response.data?.signed_url) {
        const { signed_url: signedUrl } = response.data;
        const link = document.createElement('a');
        link.href = signedUrl;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast('Download started', 'success');
      } else {
        throw new Error(response?.message || 'Failed to generate download URL');
      }
    } catch (err: any) {
      console.error('Download error:', err);
      showToast(err?.message || 'Failed to download file', 'error');
    } finally {
      setDownloadingFileId(null);
    }
  };

  // Reset loading when modal opens
  useEffect(() => {
    if (open) {
      setIsLoading(true);
    }
  }, [open]);

  // Handle completed steps data from API
  useEffect(() => {
    if (completedSteps === undefined || completedSteps === null) {
      return; // Still waiting for API response
    }
    // API response received (could be empty array or populated)
    if (!completedSteps?.length) {
      setSteps([]);
    } else {
      setSteps(completedSteps);
    }
    setIsLoading(false); // Always turn off loading when we get API response
  }, [completedSteps]);

  useEffect(() => { if (open) setError(null); }, [open]);

  useEffect(() => {
    if (open && transactionItem) {
      setTransactionId(transactionItem.uid || '');
    }
  }, [open, transactionItem]);

  const getSchemaFields = (): Field[] => {
    const allFields: Field[] = [];
    steps.forEach((step) => {
      if (!step.componentSchema?.fields) return;
      step.componentSchema.fields.forEach((schemaField: any) => {
        if (!schemaField) return;
        const matchingEventData = step.eventData.find((ed) => ed.field === schemaField.uid);
        allFields.push({
          field: schemaField.uid,
          description: matchingEventData?.description || '',
          question: schemaField.question,
          inputComponent: schemaField.inputComponent as InputComponents,
          hasAddIcon: false,
          options: schemaField.options || matchingEventData?.options || [],
          resourceType: schemaField.resourceType || matchingEventData?.resourceType,
          resourceList: schemaField.resourceList,
          dropdownSource: schemaField.dropdownSource || matchingEventData?.dropdownSource,
          settings: { ...schemaField.settings, ...matchingEventData?.settings },
          uid: schemaField.uid,
        });
      });
    });
    return allFields;
  };

  console.log('transaction item data',transactionItem.file_attachmet_list)

  const getInitialValues = (): Record<string, any> => {
    const schemaFields = getSchemaFields();
    const allEventData: EventDataItem[] = [];
    steps.forEach((step) => allEventData.push(...step.eventData));
    return patchEventValuesIntoSchema(schemaFields, allEventData);
  };
  const schemaFields = getSchemaFields();

  // When there are no editable fields we still want the full two-column layout
  // so the chat panel remains visible on the right.
  const hasNoFields = schemaFields.length === 0 && !isLoading;

  const validationSchema = Yup.object(
    schemaFields?.reduce((schema: any, field: any) => {
      // Helper function for date validation
      const dateValidationTest = (value: any) => {
        console.log('Validating date value:', value);
        if (!value) return true;
        if (typeof value === 'object' && value !== null && 'date' in value) {
          const d = new Date(value.date);
          return !isNaN(d.getTime()) && d >= new Date(1900, 0, 1);
        }
        if (value instanceof Date) return !isNaN(value.getTime()) && value >= new Date(1900, 0, 1);
        if (typeof value === 'string') {
          const d = new Date(value);
          return !isNaN(d.getTime()) && d >= new Date(1900, 0, 1);
        }
        return false;
      };

      if (field.inputComponent === InputComponents.DATE) {
        if (field.settings?.required) {
          schema[field.question] = Yup.mixed()
            .required(`${field.question} is required`)
            .test('is-valid-date', `${field.question} must be a valid date`, (value: any) => {
              if (!value) return false;
              return dateValidationTest(value);
            });
        } else {
          schema[field.question] = Yup.mixed()
          .nullable()
          .notRequired()
          .test('is-valid-date', `${field.question} must be a valid date`, (value: any) => {
            if (!value||value?.length === 0) return true; // skip validation if empty — field is not required
            return dateValidationTest(value);
          });
        }
      } else if (field.inputComponent === InputComponents.FILE || field.inputComponent === InputComponents.ESIGN || field.inputComponent === InputComponents.CAMERA) {
        schema[field.question] = Yup.array().of(Yup.mixed()).notRequired();
      } else if (field.inputComponent === InputComponents.SELECT || field.inputComponent === InputComponents.RADIO) {
        if (field.settings?.required) {
          schema[field.question] = Yup.string().required(`${field.question} is required`);
        } else {
          schema[field.question] = Yup.string().notRequired();
        }
      } else if (field.settings?.required) {
        if (field.inputComponent === InputComponents.CHECKBOX) {
          schema[field.question] = Yup.array().min(1, `${field.question} is required`).required(`${field.question} is required`);
        } else if (field.inputComponent === InputComponents.RESOURCE) {
          if (field.settings?.multiple) {
            schema[field.question] = Yup.array().min(1, `${field.question} is required`).required(`${field.question} is required`);
          } else {
            schema[field.question] = Yup.mixed().required(`${field.question} is required`).test('resource-selected', `${field.question} is required`, (value: any) => value && Boolean(value.id || value._id));
          }
        } else if (field.inputComponent === InputComponents.SWITCH) {
          schema[field.question] = Yup.mixed().test('switch-has-value', `${field.question} is required`, (value: any) => value !== null && value !== undefined && typeof value === 'object' && 'value' in value);
        } else if (field.inputComponent === InputComponents.NUMBER) {
          schema[field.question] = Yup.number().typeError(`${field.question} must be a number`).required(`${field.question} is required`);
        } else {
          schema[field.question] = Yup.string().required(`${field.question} is required`);
        }
      }
      return schema;
    }, {})
  );

  const handleSubmit = async (values: Record<string, any>, { setSubmitting }: any) => {
    setSaving(true);
    setError(null);
    try {
      // Upload CAMERA images to S3 (like the native app) so edits store file
      // refs, not base64. Existing refs / http URLs pass through unchanged.
      const uploadedValues = { ...values };
      for (const field of schemaFields) {
        if (field.inputComponent !== InputComponents.CAMERA) continue;
        const imgs = Array.isArray(values[field.question]) ? values[field.question] : [];
        const refs: any[] = [];
        for (let i = 0; i < imgs.length; i++) {
          const img = imgs[i];
          if (img && typeof img === 'object' && (img.file_path || img.public_url)) { refs.push(img); continue; }
          let file: File | null = null;
          if (img instanceof File) file = img;
          else if (img instanceof Blob) file = new File([img], `capture-${Date.now()}-${i}.jpg`, { type: (img as Blob).type || 'image/jpeg' });
          else if (typeof img === 'string' && img.startsWith('data:')) {
            const blob = await (await fetch(img)).blob();
            file = new File([blob], `capture-${Date.now()}-${i}.jpg`, { type: blob.type || 'image/jpeg' });
          } else if (typeof img === 'string' && img.startsWith('http')) { refs.push({ public_url: img, file_path: img }); continue; }
          if (!file) continue;
          const res: any = await uploadAsset(file, 'transaction/edit-event');
          const f = res?.data ?? res;
          if (f?.file_path || f?.public_url) refs.push({ file_name: f.file_name, file_path: f.file_path, mime_type: f.mime_type, public_url: f.public_url });
        }
        uploadedValues[field.question] = refs;
      }

      const eventDataMap = new Map<string, EventDataItem>();
      steps.forEach((step) => step.eventData.forEach((ed) => eventDataMap.set(ed.question, ed)));
      const groupedUpdates = groupFieldsByStep(steps, schemaFields, uploadedValues, eventDataMap);
      const apiFormattedUpdates = transformPayloadForApi(groupedUpdates, steps);
      const response = await bulkUpdateEvents(apiFormattedUpdates);
      if (response?.success) {
        showToast('Transaction updated successfully', 'success');
        onSaveSuccess?.();
        onClose();
      } else {
        throw new Error(response?.message || 'Failed to update transaction');
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save changes');
      showToast('Failed to update transaction', 'error');
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  const hypertext = (key: string, touched: any, errors: any, submitCount: number) =>
    touched[key] || submitCount > 0 ? errors[key] : '';


  const renderInputField = (
    field: Field,
    values: Record<string, any>,
    handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>,
    setFieldValue: (field: string, value: any) => void,
    errors: Record<string, any>,
    touched: Record<string, any>,
    submitCount: number,
    isEditable: boolean
  ) => {
    const keys = field.question;

    if (!isEditable) {
      const displayVal = (() => {
        const val = values[keys];
        if (field.inputComponent === InputComponents.DATE) {
          if (val && typeof val === 'object' && val.date) return new Date(val.date).toLocaleString();
          return val ? String(val) : '—';
        }
        if (field.inputComponent === InputComponents.RESOURCE) {
          if (Array.isArray(val)) {
            return val.map((v: any) => {
              if (v?.fields) return Object.values(v.fields).filter((f): f is string => typeof f === 'string').slice(0, 2).join(', ');
              return JSON.stringify(v);
            }).join(' | ');
          }
          if (val?.fields) return Object.values(val.fields).filter((f): f is string => typeof f === 'string').slice(0, 2).join(', ');
          return '—';
        }
        if (Array.isArray(val)) return val.join(', ') || '—';
        if (typeof val === 'object' && val?.value !== undefined) return String(val.value);
        return String(val || '—');
      })();
      return (
        <Box sx={{ p: '8px 10px', bgcolor: 'action.hover', borderRadius: 1, fontSize: '0.875rem', color: 'text.secondary', border: '1px solid', borderColor: 'divider' }}>
          {displayVal}
        </Box>
      );
    }

    switch (field.inputComponent) {
      case InputComponents.TEXT:
        if (field.settings?.multiple) {
          const arr = Array.isArray(values[keys]) && values[keys].length > 0
            ? values[keys]
            : values[keys] && !Array.isArray(values[keys])
              ? [values[keys]]
              : [''];
          return (
            <Box>
              {arr.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                  <IconButton size="small" color="primary" onClick={() => setFieldValue(keys, [''])}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              {arr.map((val: string, idx: number) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    type="text" fullWidth variant="outlined" size="small" value={val}
                    error={Boolean(touched[keys] && errors[keys])}
                    onChange={(e) => { const newArr = [...arr]; newArr[idx] = e.target.value; setFieldValue(keys, newArr); }}
                    multiline={field.settings?.isMultiline}
                    rows={field.settings?.rows || (field.settings?.isMultiline ? 3 : 1)}
                    sx={{ '& .MuiInputBase-input': { padding: '8px' } }}
                  />
                  {arr.length > 1 && idx !== arr.length - 1 && (
                    <IconButton size="small" sx={{ ml: 1 }} color="error" onClick={() => setFieldValue(keys, arr.filter((_: any, i: number) => i !== idx))}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  )}
                  {idx === arr.length - 1 && (
                    <IconButton size="small" sx={{ ml: 1 }} color="primary" onClick={() => setFieldValue(keys, [...arr, ''])}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          );
        }
        return (
          <TextField
            type="text" fullWidth variant="outlined" size="small" name={keys}
            value={values[keys] || ''} onChange={(e) => setFieldValue(keys, e.target.value)}
            multiline={!!field.settings?.isMultiline}
            rows={field.settings?.rows || (field.settings?.isMultiline ? 3 : 1)}
            error={Boolean((touched[keys] || submitCount > 0) && errors[keys])}
            helperText={hypertext(keys, touched, errors, submitCount)}
            sx={{ ...(field.settings?.isMultiline && {
                        '& .MuiOutlinedInput-root': {
                          padding: '0px',
                        }
                      }),
                      '& .MuiInputBase-input': {
                        padding: '10px',
                      } }}
          />
        );

      case InputComponents.NUMBER:
        return (
          <TextField
            type="number" fullWidth variant="outlined" size="small" name={keys}
            value={values[keys] || ''} onChange={handleChange}
            error={Boolean(touched[keys] && errors[keys])}
            helperText={hypertext(keys, touched, errors, submitCount)}
            sx={{ '& .MuiInputBase-input': { padding: '8px' } }}
          />
        );

      case InputComponents.PASSWORD:
        return (
          <TextField
            type={passwordVisibility[keys] ? 'text' : 'password'}
            fullWidth variant="outlined" size="small" name={keys}
            value={values[keys] || ''} onChange={(e) => setFieldValue(keys, e.target.value)}
            error={Boolean(touched[keys] && errors[keys])}
            helperText={hypertext(keys, touched, errors, submitCount)}
            sx={{ '& .MuiInputBase-input': { padding: '8px' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleTogglePassword(keys)} edge="end" size="small">
                    {passwordVisibility[keys] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        );

      case InputComponents.FILE:
        return (
          <>
            <MultiFileUploader value={values[keys]} onChange={(files) => setFieldValue(keys, files)} maxSize={15} entityId={transactionItem?.uid} entityType="authkey" />
            {Array.isArray(values[keys]) && values[keys].length > 0 && (
              <Box mt={1.5} display="flex" flexWrap="wrap" gap={1}>
                {values[keys].map((file: any, idx: number) => {
                  const fileType = file.mime_type || file.type || '';
                  const isImage = fileType.startsWith('image/');
                  return (
                    <Box key={idx} sx={{ width: 56, height: 56, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isImage ? 'transparent' : 'grey.50', p: '6px' }} title={file.file_name || file.name}>
                      {isImage && file.url ? (
                        <img src={file.url} alt={file.file_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <InsertDriveFileIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                          <Typography sx={{ fontSize: '7px', color: 'text.secondary', fontWeight: 600 }}>
                            {fileType ? fileType.split('/')[1]?.substring(0, 3).toUpperCase() || 'FILE' : 'FILE'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors[keys]}</Typography>
            )}
          </>
        );

      case InputComponents.DATE:
        return (
          <DateTimePickerWithTimezone
            value={values[keys]}
            onChange={(newValue: any) => setFieldValue(keys, newValue)}
            includeTime={field.settings?.isTimeRequired}
            showTimezone={field.settings?.includeTimezone !== false}
            error={Boolean((touched[keys] || submitCount > 0) && errors[keys])}
            helperText={touched[keys] || submitCount > 0 ? errors[keys] : ''}
            fullWidth={true}
            required={field.settings?.required}
          />
        );

      case InputComponents.SELECT:
        return (
          <TextField
            fullWidth select variant="outlined" size="small" name={keys}
            value={values[keys] || ''} onChange={handleChange}
            error={Boolean((touched[keys] || submitCount > 0) && errors[keys])}
            helperText={touched[keys] || submitCount > 0 ? errors[keys] : ''}
            sx={{ '& .MuiInputBase-input': { padding: '8px' } }}
          >
            <MenuItem value="">Please select</MenuItem>
            {field.options?.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </TextField>
        );

      case InputComponents.CHECKBOX:
        return (
          <Box>
            <FormGroup row>
              {field.options?.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      size="small"
                      checked={Array.isArray(values[keys]) && values[keys].includes(option)}
                      onChange={(e) => {
                        const curr = Array.isArray(values[keys]) ? values[keys] : [];
                        setFieldValue(keys, e.target.checked ? [...curr, option] : curr.filter((v: string) => v !== option));
                      }}
                    />
                  }
                  label={<Typography variant="body2">{option}</Typography>}
                />
              ))}
            </FormGroup>
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography color="error" variant="caption">{errors[keys]}</Typography>
            )}
          </Box>
        );

      case InputComponents.RADIO:
        return (
          <Box>
            <RadioGroup row name={keys} value={values[keys]} onChange={handleChange}>
              {field.options?.map((opt) => (
                <FormControlLabel key={opt} value={opt} control={<Radio size="small" />} label={<Typography variant="body2">{opt}</Typography>} />
              ))}
            </RadioGroup>
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography variant="caption" color="error">{errors[keys]}</Typography>
            )}
          </Box>
        );

      case InputComponents.CAMERA:
        if (field.settings?.cameraMode === 'IMAGE') {
          return (
            <>
              <Button variant="outlined" size="small" fullWidth onClick={() => { setCameraOpen(true); setCameraField(keys); }}>
                Open Camera
              </Button>
              <Box mt={1.5} display="flex" gap={1.5} flexWrap="wrap">
                {Array.isArray(values[keys]) && values[keys].map((img: any, idx: number) => (
                  <Box key={idx} position="relative">
                    {typeof img === 'string' ? (
                      <img src={img} alt={`snap-${idx}`} width={80} height={80} style={{ objectFit: 'cover', borderRadius: 4 }} />
                    ) : img instanceof File || img instanceof Blob ? (
                      <img src={URL.createObjectURL(img)} alt={`snap-${idx}`} width={80} height={80} style={{ objectFit: 'cover', borderRadius: 4 }} />
                    ) : null}
                    <IconButton size="small" onClick={() => setFieldValue(keys, values[keys].filter((_: any, i: number) => i !== idx))} sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white', boxShadow: 1 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
              {(touched[keys] || submitCount > 0) && errors[keys] && (
                <Typography variant="caption" color="error" sx={{ display: 'block' }}>{errors[keys]}</Typography>
              )}
              <Dialog open={cameraOpen && cameraField === keys} onClose={() => setCameraOpen(false)} fullWidth maxWidth="sm" slotProps={{ backdrop: { sx: { zIndex: 1300 } } }}>
                <DialogTitle>Capture Photo</DialogTitle>
                <DialogContent>
                  <Camera
                    ref={cameraRef}
                    aspectRatio={16 / 9}
                    facingMode={['user', 'front'].includes(field.settings?.cameraFacing) ? 'user' : 'environment'}
                    errorMessages={{ noCameraAccessible: 'No camera detected.', permissionDenied: 'Camera access was denied.', switchCamera: 'Unable to switch camera.', canvas: 'Canvas not supported.' }}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setCameraOpen(false)}>Cancel</Button>
                  <Button variant="contained" onClick={() => { if (cameraRef.current) { const photo = cameraRef.current.takePhoto(); setFieldValue(cameraField, [...(values[cameraField] || []), photo]); setCameraOpen(false); } }}>Capture</Button>
                </DialogActions>
              </Dialog>
            </>
          );
        }
        return (
          <>
            <MultiFileUploader value={values[keys]} onChange={(files) => setFieldValue(keys, files)} maxSize={50} entityId={transactionItem?.uid} entityType="video" />
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography variant="caption" color="error" sx={{ display: 'block' }}>{errors[keys]}</Typography>
            )}
          </>
        );

      case InputComponents.ESIGN:
        return (
          <>
            <MultiFileUploader value={values[keys]} onChange={(files) => setFieldValue(keys, files)} maxSize={3} entityId={transactionItem?.uid} entityType="authkey" />
            {Array.isArray(values[keys]) && values[keys].length > 0 && (
              <Box mt={1.5} display="flex" flexWrap="wrap" gap={1}>
                {values[keys].map((file: any, idx: number) => {
                  const fileType = file.mime_type || file.type || '';
                  const isImage = fileType.startsWith('image/');
                  return (
                    <Box key={idx} sx={{ width: 56, height: 56, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isImage ? 'transparent' : 'grey.50', p: '6px' }}>
                      {isImage && file.url ? (
                        <img src={file.url} alt={file.file_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <InsertDriveFileIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                          <Typography sx={{ fontSize: '7px', color: 'text.secondary', fontWeight: 600 }}>
                            {fileType ? fileType.split('/')[1]?.substring(0, 3).toUpperCase() || 'FILE' : 'FILE'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography variant="caption" color="error" sx={{ display: 'block' }}>{errors[keys]}</Typography>
            )}
          </>
        );

      case InputComponents.SWITCH:
        return (
          <Box display="flex" alignItems="center" gap={2} sx={{ py: 0.5 }}>
            <IOSSwitch checked={Boolean(values[keys]?.value)} onChange={(e) => setFieldValue(keys, { value: e.target.checked })} />
            {field.settings?.label && <Typography variant="body2">{field.settings.label}</Typography>}
          </Box>
        );

      case InputComponents.RESOURCE:
        return (
          <ResourceSelector
            field={field} value={values[keys]}
            onChange={(value) => setFieldValue(keys, value)}
            resourceType={field.resourceType || field.settings?.resourceType}
            resourceList={(field as any).resourceList}
            multiple={field.settings?.multiple}
          />
        );

      default:
        return (
          <TextField
            fullWidth variant="outlined" size="small" name={keys}
            value={values[keys] || ''} onChange={handleChange}
            error={Boolean((touched[keys] || submitCount > 0) && errors[keys])}
            helperText={touched[keys] || submitCount > 0 ? errors[keys] : ''}
            sx={{ '& .MuiInputBase-input': { padding: '8px' } }}
          />
        );
    }
  };



  // ─── Main dialog ──────────────────────────────────────────────────────────
  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 1, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' } }}
    >
      {/* Column Header as DialogTitle */}
      <DialogTitle
        sx={{
          position: 'relative',
          pl: 2.5,
          pr: 1.5,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between', // 👈 changed to space-between
          gap: 1,
          bgcolor: 'primary.main',
        }}
      >
        {/* Left: Edit Transaction label */}
        <Typography
          // variant="subtitle2"
          fontSize={'13px'}
          color="white"
          fontWeight="bold"
          textTransform="uppercase"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Edit Transaction
        </Typography>

        {/* Center: Transaction label + key badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="subtitle2"
            color="white"
            fontWeight="bold"
            textTransform="uppercase"
          >
            {transactionItem?.team.settings.transactionLabel || 'Transaction'}:
          </Typography>

          <Typography
            component="span"
            sx={{
              color: '#fff',
              fontWeight: 700,
              fontSize: '12px',
              px: '10px',
              py: '5px',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: 1,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              textTransform: 'uppercase',
            }}
          >
            {transactionItem?.key ?? transactionItem?.uid ?? '-'}
          </Typography>
        </Box>

        {/* Right: Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {transactionItem?.file_attachmet_list &&
            transactionItem.file_attachmet_list.length > 0 && (
              <IconButton
                size="small"
                onClick={() => setAttachmentsOpen(true)}
                sx={{
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                  position: 'relative',
                }}
              >
                <AttachFileIcon sx={{ fontSize: 20 }} />
                <Box
                  sx={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    bgcolor: 'error.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: 'bold',
                  }}
                >
                  {transactionItem.file_attachmet_list.length}
                </Box>
              </IconButton>
            )}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
            }}
            title="Close"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>      
      <DialogContent
        sx={{
          pt: 0, pb: 0, px: 0,
          height: '70vh',
          maxHeight: '70vh',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 0,
          overflow: 'hidden',
        }}
      >
        {/* Global error banner */}
        {error && (
          <Alert severity="error" sx={{ gridColumn: '1 / -1', mx: 2, mb: 2, fontSize: '0.8rem' }}>{error}</Alert>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            LEFT COLUMN — Edit form  OR  "no fields" empty state
            The column always renders so the right-side chat stays visible.
        ══════════════════════════════════════════════════════════════════ */}
        <Box sx={{ borderRight: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ borderRight: '1px solid', borderColor: 'divider', flex: 1, overflowY: 'auto', overflowX: 'hidden', '&::-webkit-scrollbar': { width: 5 }, '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: 4 } }}>
            {isLoading ? (
              // Loading spinner
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 6 }}>
                <CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
              </Box>

            ) : hasNoFields ? (
              // ── No editable fields — keep two-column layout so chat shows ──
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box
                  sx={{
                    py: 3, px: 3,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    flex: 1,
                    minHeight: 0
                  }}
                >
                  <EditNoteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary" variant="body2" align="center">
                    No editable fields found for this transaction.
                  </Typography>
                  <Button
                    variant="outlined" size="small" onClick={onClose}
                    startIcon={<CancelIcon fontSize="small" />}
                    sx={{ mt: 3, textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                  >
                    Close
                  </Button>
                </Box>
              </Box>

            ) : (
              // ── Normal edit form ──
              <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={handleSubmit}
              >
                {({ values, handleChange, handleSubmit, setFieldValue, touched, errors, submitCount }) => (
                  <form onSubmit={handleSubmit} id="edit-transaction-form" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Scrollable content area */}
                    <Box sx={{
                      flex: 1,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      '&::-webkit-scrollbar': { width: 5 },
                      '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: 4 }
                    }}>
                      {/* Step sections */}
                      {steps.map((step, stepIdx) => {
                        const schemaMap = new Map(
                          step.componentSchema?.fields?.map((f) => [f.uid, f]) ?? []
                        );
                        const stepFields = schemaFields.filter((field) =>
                          step.componentSchema?.fields?.some((sf) => sf.uid === field.uid)
                        );
                        if (stepFields.length === 0) return null;

                        return (
                          <Box key={step.stepUid} sx={{ p: '8px' }}>
                            {/* Step header bar */}
                            <Box sx={{ background: theme.palette.primary.main, color: '#fff', px: 2, py: 0.8, display: 'flex', alignItems: 'center', gap: 1, mb: '10px',borderRadius:'8px 8px 0 0' }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>
                                {step.stepTitle}
                              </Typography>
                            </Box>

                            {/* Step fields grid */}
                            <Box sx={{ px: 1.8, py: "4px" }}>
                              <Grid container spacing={2}>
                                {stepFields.map((field) => {
                                  const schema = schemaMap.get(field.uid);
                                  const isEditable = schema?.settings?.isEditable !== false;
                                  return (
                                    <Grid item xs={12} md={6} key={field.uid} sx={{ paddingTop: "8px !important", paddingLeft: "8px !important" }}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, fontSize: '12px', color: 'text.primary', mb: '1px', display: 'flex', alignItems: 'center', gap: 0.5 }}
                                      >
                                        {field.question}
                                        {field.settings?.required && (
                                          <Typography component="span" color="error" sx={{ lineHeight: 1 }}>*</Typography>
                                        )}
                                        {!isEditable && (
                                          <Typography component="span" sx={{ fontSize: '0.65rem', color: 'text.disabled', ml: 0.5 }}>(read-only)</Typography>
                                        )}
                                      </Typography>
                                      {renderInputField(field, values, handleChange, setFieldValue, errors, touched, submitCount, isEditable)}
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Box>

                            {stepIdx < steps.length - 1 && <Divider />}
                          </Box>
                        );
                      })}
                    </Box>
                  </form>
                )}
              </Formik>
            )}
          </Box>

          {/* Form footer actions — STICKY TO BOTTOM, OUTSIDE FORM */}
          {!hasNoFields && !isLoading && (
            <Box
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                px: 2.5,
                py: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.5,
                bgcolor: 'grey.50',
                boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
                flexShrink: 0,
              }}
            >
              <Button
                form="edit-transaction-form" type="submit" variant="contained" size="small" disabled={saving}
                startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <SaveIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, px: 2.5, bgcolor: theme.palette.primary.main, '&:hover': { bgcolor: theme.palette.primary.main,opacity:0.7 } }}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </Box>
          )}
        </Box>

        {/* ══════════════════════════════════════════════════════════════════
            RIGHT COLUMN — Chat (always visible regardless of fields)
        ══════════════════════════════════════════════════════════════════ */}
        <Box 
          sx={{
            gridColumn: 2,
            bgcolor: 'grey.50',
            borderLeft: '1px solid', borderColor: 'divider',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',    // ← allow StartChatPanel to control its own scroll
            height: '100%',
          }}
        >
          {transactionId ? (
            currentChatThread ? (
              <InlineChatWindow
                open={true}
                onClose={handleBackToCreateChat}
                thread={currentChatThread}
                transactionKey={transactionItem?.key || transactionItem?.transactionKey || transactionId}
                embedded={true}
              />
            ) : (
                <StartChatPanel
                  onBack={onClose}
                  transactionId={transactionId}
                  transactionKey={transactionItem?.key || transactionItem?.transactionKey || transactionId}
                  onChatCreated={handleChatCreated}
                  embedded={true}
                />
            )
          ) : (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography color="text.secondary" variant="body2" align="center">
                Loading chat interface...
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>

    {/* Attachments Dialog */}
    <AttachmentsDialog
        open={attachmentsOpen}
        onClose={() => setAttachmentsOpen(false)}
        transactionItem={transactionItem}
        downloadingFileId={downloadingFileId}
        onDownload={handleDownloadFile}
      />
    </>
  );
};

export default EditTransactionModal;